/**
 * The one place that knows what a FieldIQ API response envelope looks like.
 *
 * WHY THIS EXISTS
 * ---------------
 * `res.data?.data || res.data` is wrong, and it is wrong in the direction that
 * hides itself. `/api/v1/*` answers `{ success: true, data: <payload> }`. When
 * the payload is legitimately absent the server still sends the envelope:
 *
 *     { success: true, data: null }
 *
 * `res.data?.data` is then `null` — falsy — so `||` fires and hands the caller
 * THE ENVELOPE. The envelope is an object, so `if (x)` passes, `x.id` is
 * `undefined`, `x.total` is `undefined`, and the screen renders a phantom
 * record instead of an empty state. That is the bug that stamped a hardcoded
 * `biweekly` default onto tenants with no pay-period config: GET
 * /pay-periods/config returns `data: null` for a company that has never
 * configured one, and the fallthrough turned "unconfigured" into "an object".
 *
 * There is a second shape the `||` cannot see at all. When a handler returns
 * `data: someUndefinedValue`, JSON.stringify DROPS the key, so the response is
 * `{ success: true }` with no `data` at all. A presence test written as
 * `'data' in body` reads that as "not an envelope" and falls through to the
 * envelope just the same. Both shapes have to mean the same thing: null.
 *
 * THE CONTRACT
 * ------------
 * `unwrap` returns the payload, or `null` when the server sent no payload. It
 * never returns the envelope. `null` is a real answer the caller has to handle
 * — an empty state, an error, a redirect — not something to paper over with a
 * default object.
 *
 * Sibling metadata (`sentTo`, `requiresEmail`, `customerName`, `message`) lives
 * on the envelope, NOT inside `data`. Read those off `res.data` directly; they
 * were never reachable through the payload and `unwrap` does not pretend
 * otherwise.
 */

/** A `/api/v1/*` success envelope. Sibling metadata keys are open-ended. */
export interface ApiEnvelope<T> {
  success?: boolean;
  data?: T | null;
  error?: string;
  [key: string]: unknown;
}

/**
 * Is this the envelope, or is it already the payload?
 *
 * A handful of older endpoints answer with the bare payload and no envelope at
 * all, so this cannot just assume. Two independent signals, either of which is
 * enough:
 *
 *   - a `success` key — no domain entity in this API carries one; and it is the
 *     ONLY signal available for `{ success: true }` with the `data` key dropped.
 *   - a `data` key — covers the (rare) enveloped response with no `success`.
 *
 * Arrays are never envelopes: a list endpoint answering with a bare array is
 * already the payload.
 */
function isEnvelope(body: unknown): body is ApiEnvelope<unknown> {
  if (typeof body !== 'object' || body === null || Array.isArray(body)) return false;
  return 'success' in body || 'data' in body;
}

/**
 * Unwrap a response body to its payload.
 *
 * Pass `res.data` (the axios body), not `res`.
 *
 *     const ticket = unwrap<RawTicket>(res.data);
 *     if (!ticket) return <NotFound />;   // honest empty state
 *
 * Returns `null` — never the envelope — when the server sent `data: null`, sent
 * no `data` key, or sent nothing at all.
 */
export function unwrap<T>(body: unknown): T | null {
  if (body === null || body === undefined) return null;
  if (!isEnvelope(body)) return body as T;
  const payload = body.data;
  return payload === undefined || payload === null ? null : (payload as T);
}

/**
 * Unwrap a response body to a list.
 *
 * Stricter than `unwrap<T[]>(body) ?? []`: a payload that is not actually an
 * array becomes `[]` rather than being handed to `.map` as an object. That is
 * the same guarantee the old `|| []` accidentally provided, made deliberate.
 */
export function unwrapArray<T>(body: unknown): T[] {
  const payload = unwrap<unknown>(body);
  return Array.isArray(payload) ? (payload as T[]) : [];
}
